import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, Clock3, ShieldCheck } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '@/pages/public/landing/components/Header';
import { Footer } from '@/pages/public/landing/components/Footer';

const tutorialImages = {
	cadastroInicial:
		'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1776880878/foto_1_nttbj1.jpg',
	cadastroPreenchido:
		'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1776880878/foto_2_mvlchl.jpg',
	boasVindas:
		'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1776880878/foto_3_mybayx.jpg',
	visaoGeral:
		'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1776880878/foto_4_fmmqvp.jpg',
	vamos: 'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1776880878/foto_5_nk8xkq.jpg',
};

const StepCard = ({
	index,
	title,
	description,
	bullets,
	children,
}: {
	index: number;
	title: string;
	description: string;
	bullets?: string[];
	children?: React.ReactNode;
}) => (
	<article className="rounded-[2rem] border border-stone-200 bg-white p-6 md:p-8">
		<div className="mb-4 flex items-center gap-3">
			<span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange text-sm font-black text-white shadow-[4px_4px_0px_0px_rgba(255,113,70,0.35)]">
				{index}
			</span>
			<h2 className="text-xl font-black tracking-tight text-stone-900 md:text-2xl">
				{title}
			</h2>
		</div>

		<p className="text-sm leading-relaxed text-stone-500 md:text-base">
			{description}
		</p>

		{bullets && bullets.length > 0 && (
			<ul className="mt-4 space-y-2">
				{bullets.map((item) => (
					<li
						key={item}
						className="flex items-start gap-2 text-sm text-stone-600"
					>
						<Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-orange" />
						<span>{item}</span>
					</li>
				))}
			</ul>
		)}

		{children}
	</article>
);

const TutorialPage = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const handleGoToOnboarding = () => {
		const companyName = searchParams.get('empresa');
		const query = companyName
			? `?empresa=${encodeURIComponent(companyName)}`
			: '';
		navigate(`/onboarding/colaborador${query}`);
	};

	return (
		<section className="flex min-h-screen w-full flex-col bg-stone-50 text-stone-900">
			<Helmet>
				<title>Tutorial do Colaborador | Plumaa</title>
				<meta
					name="description"
					content="Guia rapido para colaborador criar acesso e iniciar o primeiro formulario na Plumaa."
				/>
			</Helmet>

			<Header />

			<main className="w-full px-4 py-8 md:px-8 md:py-12">
				<div className="mx-auto max-w-5xl space-y-8">
					<section className="rounded-[2.5rem] border border-stone-200 bg-white p-7 md:p-10">
						<p className="text-[10px] font-black uppercase tracking-[0.22em] text-brand-orange">
							Tutorial do colaborador
						</p>
						<h1 className="mt-3 text-3xl font-black tracking-tight text-stone-900 md:text-5xl">
							Sua jornada na Plumaa em 6 passos.
						</h1>
						<p className="mt-3 max-w-3xl text-sm leading-relaxed text-stone-500 md:text-base">
							Em cerca de 10 minutos voce cria seu acesso, avanca
							pelas telas iniciais e comeca seu primeiro
							formulario, tudo pelo navegador.
						</p>

						<div className="mt-6 flex flex-wrap gap-2">
							<span className="inline-flex items-center gap-2 rounded-full bg-brand-orange/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-brand-orange">
								<Clock3 className="h-3.5 w-3.5" />
								10 minutos
							</span>
							<span className="inline-flex items-center gap-2 rounded-full bg-stone-100 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-stone-600">
								100% online
							</span>
							<span className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-emerald-700">
								<ShieldCheck className="h-3.5 w-3.5" />
								Confidencial
							</span>
						</div>
					</section>

					<section className="rounded-[2rem] border border-emerald-200 bg-emerald-50 p-5 md:p-6">
						<p className="text-sm leading-relaxed text-emerald-800">
							<span className="font-black">
								Suas respostas sao individuais e confidenciais.
							</span>{' '}
							Sua empresa recebe apenas metricas agregadas do
							grupo, nunca o que cada pessoa respondeu.
						</p>
					</section>

					<section className="space-y-5">
						<StepCard
							index={1}
							title="Abra o link de acesso"
							description="No celular ou computador, clique no botao abaixo para iniciar o fluxo."
						>
							<button
								type="button"
								onClick={handleGoToOnboarding}
								className="mt-5 inline-flex items-center justify-center gap-2 rounded-full border-2 border-black bg-brand-orange px-12 py-4 text-xs font-black uppercase tracking-[0.15em] text-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all duration-150 hover:translate-x-[2px] hover:translate-y-[2px] hover:bg-brand-orange/90 hover:shadow-none active:scale-90"
							>
								Preencha o formulario
								<ArrowRight className="h-4 w-4" />
							</button>
						</StepCard>

						<StepCard
							index={2}
							title="Preencha seu cadastro"
							description="Informe seus dados pessoais, crie a senha e aceite os termos para finalizar o cadastro."
							bullets={[
								'Nome, sobrenome, e-mail e telefone',
								'Data de nascimento e CPF',
								'Senha e confirmacao de senha',
								'Aceite dos termos de uso e politica de privacidade',
							]}
						>
							<div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
								<div className="overflow-hidden rounded-2xl border border-stone-200 bg-stone-50">
									<img
										src={tutorialImages.cadastroInicial}
										alt="Tela inicial de cadastro do colaborador"
										className="h-full w-full object-cover"
										loading="lazy"
									/>
								</div>
								<div className="overflow-hidden rounded-2xl border border-stone-200 bg-stone-50">
									<img
										src={tutorialImages.cadastroPreenchido}
										alt="Tela de cadastro preenchida"
										className="h-full w-full object-cover"
										loading="lazy"
									/>
								</div>
							</div>
						</StepCard>

						<StepCard
							index={3}
							title="Leia a tela de boas-vindas"
							description="Depois do cadastro, voce vera a mensagem inicial da jornada. Clique em Continuar."
						>
							<div className="mt-5 overflow-hidden rounded-2xl border border-stone-200 bg-stone-50">
								<img
									src={tutorialImages.boasVindas}
									alt="Tela de boas-vindas do colaborador"
									className="w-full"
									loading="lazy"
								/>
							</div>
						</StepCard>

						<StepCard
							index={4}
							title="Passe pela visao geral"
							description="A proxima tela apresenta como o acompanhamento funciona. Clique em Avancar para seguir."
						>
							<div className="mt-5 overflow-hidden rounded-2xl border border-stone-200 bg-stone-50">
								<img
									src={tutorialImages.visaoGeral}
									alt="Tela de visao geral do acompanhamento"
									className="w-full"
									loading="lazy"
								/>
							</div>
						</StepCard>

						<StepCard
							index={5}
							title="Confirme que vai comecar"
							description="Na ultima tela de introducao, clique em Vamos para iniciar o formulario."
						>
							<div className="mt-5 overflow-hidden rounded-2xl border border-stone-200 bg-stone-50">
								<img
									src={tutorialImages.vamos}
									alt="Tela final de introducao antes do formulario"
									className="w-full"
									loading="lazy"
								/>
							</div>
						</StepCard>

						<StepCard
							index={6}
							title="Responda o formulario"
							description="Escolha um momento tranquilo e responda com sinceridade. Nao existe resposta certa ou errada."
							bullets={[
								'Nao e avaliacao de desempenho individual',
								'Tempo medio de resposta: cerca de 10 minutos',
								'Voce pode pular perguntas sensiveis, se precisar',
							]}
						/>
					</section>

					<section className="rounded-[2.5rem] border border-brand-orange/20 bg-gradient-to-br from-brand-orange/10 to-brand-orange/20 p-8 text-center md:p-10">
						<h2 className="text-2xl font-black tracking-tight text-stone-900 md:text-3xl">
							Pronto para comecar?
						</h2>
						<p className="mx-auto mt-2 max-w-2xl text-sm text-stone-500 md:text-base">
							Reserve alguns minutos do seu dia e conclua o
							onboarding.
						</p>
						<button
							type="button"
							onClick={handleGoToOnboarding}
							className="mt-6 inline-flex items-center justify-center gap-2 rounded-full border-2 border-black bg-brand-orange px-12 py-4 text-xs font-black uppercase tracking-[0.15em] text-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all duration-150 hover:translate-x-[2px] hover:translate-y-[2px] hover:bg-brand-orange/90 hover:shadow-none active:scale-90"
						>
							Comecar agora
							<ArrowRight className="h-4 w-4" />
						</button>
					</section>
				</div>
			</main>

			<Footer />
		</section>
	);
};

export { TutorialPage };
